"use client"

import { useEffect } from "react"

/**
 * Traz o campo focado para cima do teclado virtual.
 *
 * Em WebView (Expo), `visualViewport` e `interactiveWidget` nem sempre
 * respondem, então a rolagem é feita na mão: ao focar um campo que ficou
 * embaixo do teclado, ele sobe.
 *
 * Três regras que este hook aprendeu quebrando a tela:
 *
 * 1. **Só onde existe teclado virtual.** No desktop não há teclado cobrindo
 *    nada, e rolar no foco só atrapalha: a pessoa clica num campo no pé da tela
 *    e a página se mexe sozinha debaixo dela. Pior, `scrollIntoView` sobe por
 *    **todos** os contêineres roláveis do caminho, e não só pelo conteúdo da
 *    página: era isso que empurrava a casca inteira para cima.
 *
 * 2. **Só quando o campo está mesmo escondido.** Rolar um campo que já está à
 *    vista é movimento gratuito, e movimento gratuito parece defeito.
 *
 * 3. **A pessoa manda.** As tentativas seguem a animação do teclado (100, 350 e
 *    600ms), mas qualquer gesto de rolagem no meio disso cancela o que falta.
 *    Sem isso, quem rolava para cima logo depois de tocar num campo era puxado
 *    de volta para baixo pela tentativa seguinte, brigando com a própria tela.
 */
export function useKeyboardScroll() {
  useEffect(() => {
    if (typeof window === "undefined") return
    // Dedo, não cursor: é o que separa "tem teclado virtual" de "não tem".
    if (!window.matchMedia?.("(pointer: coarse)").matches) return

    let timers: ReturnType<typeof setTimeout>[] = []

    const cancelar = () => {
      timers.forEach(clearTimeout)
      timers = []
    }

    /** Quanto da tela sobra depois do teclado. */
    const alturaVisivel = () => window.visualViewport?.height ?? window.innerHeight

    function subir(el: HTMLElement) {
      if (document.activeElement !== el) return
      const caixa = el.getBoundingClientRect()
      // Já está à vista: mexer aqui é movimento gratuito.
      if (caixa.bottom <= alturaVisivel() - 8 && caixa.top >= 0) return
      el.scrollIntoView({ behavior: "smooth", block: "center" })
    }

    function aoFocar(e: FocusEvent) {
      const alvo = e.target as HTMLElement | null
      if (!alvo) return
      const ehCampo =
        alvo instanceof HTMLInputElement ||
        alvo instanceof HTMLTextAreaElement ||
        alvo.getAttribute("contenteditable") === "true"
      if (!ehCampo) return

      cancelar()
      // Android abre o teclado em ~250ms, iOS em ~300ms, e alguns aparelhos
      // demoram 500ms: as três tentativas cobrem a faixa toda.
      timers.push(setTimeout(() => subir(alvo), 100))
      timers.push(setTimeout(() => subir(alvo), 350))
      timers.push(setTimeout(() => subir(alvo), 600))
    }

    document.addEventListener("focusin", aoFocar, { passive: true })
    document.addEventListener("focusout", cancelar, { passive: true })
    /* Gesto da pessoa cancela o que ainda não aconteceu: a partir daí quem
       decide onde a tela está é ela.

       Só gesto, e não o evento `scroll`: a nossa própria rolagem suave dispara
       `scroll`, e ouvir isso faria a primeira tentativa cancelar as outras
       duas, justamente as que corrigem a posição depois que o teclado terminou
       de subir. */
    document.addEventListener("touchmove", cancelar, { passive: true })
    document.addEventListener("wheel", cancelar, { passive: true })

    return () => {
      document.removeEventListener("focusin", aoFocar)
      document.removeEventListener("focusout", cancelar)
      document.removeEventListener("touchmove", cancelar)
      document.removeEventListener("wheel", cancelar)
      cancelar()
    }
  }, [])
}
